import { Router } from 'aurelia-router';
import { autoinject, camelCase } from 'aurelia-framework';
import { HttpClient, json } from 'aurelia-fetch-client';
import { EventAggregator } from 'aurelia-event-aggregator';
import {
  IApiResponse, IRegisterModel, ILoginModel, IUserInfo, ITrialVideo, ITrialMaterial,
  IResetPasswordModel, IConfirmAccountModel, IChangePasswordModel,
  IChangeUserNameModel, IPurchase, IBasketItemTransactionItem,
  IGetDataParams, IEntityPerson, IZoomRegisterModel, IConfirmChangeEmailModel,
  IProductOption, IEngagement, IPurchases,
} from '@shared/interfaces/interfaces';
import { setBreezeHeaders } from '@data/entity-manager-factory';
import { Md5 } from './md5';
import { SentryValueConverter } from '@shared/value-converters/sentry';
import isBefore from 'date-fns/isBefore';
import startOfDay from 'date-fns/startOfDay';
import isEqual from 'date-fns/isEqual';
// import * as toCamelCase from 'camelcase-keys';
// import { resolve, reject } from 'bluebird';

@autoinject
export class IdentityHelpers {

  private router: Router;
  public showContent: boolean = false;
  public refreshData: boolean = false;
  public httpClient: HttpClient;
  private md5: Md5;
  private sentry: SentryValueConverter;
  public firstRequest: boolean = true;
  readonly baseAddress: string = '/api/account';
  public user: IUserInfo = {
    IsAuthenticated: false,
    UserName: '',
    Roles: [],
    AntiForgeryToken: '',
    IsFullyConfirmed: false,
    PersonId: '',
    IsActiveCommunity: false,
    CommunityExpired: true,
    Person: null,
    Purchases: [],
    CourseProductOptions: [],
    SessionEngagement: [],
    HadCommunity: false,
    CancelledCommunity: false,
    TrialCommunity: false,
    Trial7Days: false,
    Webinars: [],
  };
  public defaultPaymentMethod: any = null;
  public hideNav: boolean = false;
  public isCourses: boolean = false;
  public antiForgeryDone: boolean = false;
  public forceAntiForgeryUpdate: boolean = false;
  private ea: EventAggregator;
  public gotUserInfo: boolean = false;
  public status: string = '';
  public error406: string =
    'Error: we protect our site against attacks by using a cookie, which is missing or invalid. Please refresh the page and try again. ' +
    'If you have disabled cookies, you will need to enable them for this site in order for it to work properly. ' +
    'If you continue to see this message, your security cookie might have expired. Please clear all browser cookies for ' +
    'thecuriouspianoteachers.org and try again - everything should then work as normal. We apologise for any inconvenience.';

  constructor(router: Router, ea: EventAggregator, md5: Md5, sentry: SentryValueConverter) {
    this.router = router;
    this.httpClient = new HttpClient();
      // .configure((x: any) => {
        // x.defaults.cache = 'no-cache';
      // });
    this.ea = ea;
    this.md5 = md5;
    this.sentry = sentry;
    // this.lookups = lookups;
  }

  hasExpired(date: Date) {
    return !isBefore(startOfDay(new Date()), date);
  }

  hasExpiredOrEqualTo(date: Date) {
    return !isBefore(startOfDay(new Date()), startOfDay(date))
      || isEqual(startOfDay(new Date()), startOfDay(date));
  }

  buildPurchases(person: IEntityPerson, forAdmin?: boolean): IPurchases {

    const purchaseHistory: IPurchases = {
      purchases: [], courseProductOptions: [], hadCommunity: false,
      cancelledCommunity: false, trialCommunity: false,
      trial7days: false, pendingCommunityCancellation: false,
    };
    let purchase: IPurchase;
    let courseProductOption: IProductOption;
    let transactionItems: IBasketItemTransactionItem[];
    let transactionItem: IBasketItemTransactionItem;
    const userPurchases: IPurchase[] = [];
    const courseProductOptions: IProductOption[] = [];
    let hadCommunity: boolean = false;
    let cancelledCommunity: boolean = false;
    let trialCommunity: boolean = false;
    let trial7days: boolean = false;
    let pendingCancelledCommunity: boolean = false;

    // if (this.user?.Person) {

      // this.user.Purchases = [];
      // console.log(this.user.Person.orders);

      // for (const order of this.user.Person.orders) {

    const orders: any = person.orders;

    orders.sort((a: any, b: any) => {
      const dateA: any = new Date(a.createdDate);
      const dateB: any = new Date(b.createdDate);
      return dateB - dateA;
    });

    for (const order of orders) {

      order.basket.basketItems.sort((a: any, b: any) => {
        const dateA: any = new Date(a.updatedDate);
        const dateB: any = new Date(b.updatedDate);
        return dateB - dateA;
      });

      for (const basketItem of order.basket.basketItems) {

        if ((basketItem.isActive || (person.isCommunity && !person.isActiveCommunity))
          || basketItem.isCancelled) {

          if (basketItem.subscriptionId && basketItem.subscriptionId.length > 0) {
            hadCommunity = true;
            cancelledCommunity = basketItem.isCancelled;

            if (cancelledCommunity && person.communityExpires) {
              pendingCancelledCommunity = !this.hasExpiredOrEqualTo(person.communityExpires);
            }            

            if (person.isActiveCommunity &&
              basketItem.isActive &&
              basketItem.firstBillingDate &&
              !this.hasExpired(basketItem.firstBillingDate)) {
              trialCommunity = true;
              trial7days = basketItem.productOption.planId.indexOf('7days') > -1;
            }

          }

          transactionItems = [];

          purchase = {
            orderDate: order.createdDate,
            startDate: basketItem.productOption.startDate,
            basketItemName: basketItem.itemName,
            productName: basketItem.product.name,
            productIsCommunity: basketItem.product.isCommunity,
            productRequiresCommunity: basketItem.product.requiresCommunity,
            productOptionId: basketItem.productOptionId,
            productOptionName: basketItem.productOption.name,
            productOptionPriceDesc: basketItem.productOption.priceDescription,
            productOptionCurrency: basketItem.productOption.currency,
            productOptionSchedule: basketItem.productOption.paymentSchedule,
            productOptionGroupId: basketItem.productOption.productOptionGroupId,
            productOptionUpgradeId: basketItem.productOption.productOptionUpgradeId,
            productOptionPlanId: basketItem.productOption.planId,
            basketItemId: basketItem.basketItemId,
            basketItemQuantity: basketItem.quantity,
            basketItemPrice: basketItem.price,
            basketItemDiscountCode: basketItem.discountCode,
            basketItemDietaryInfo: basketItem.dietaryInfo,
            basketHowHeard: basketItem.basket.howHeard,
            basketItemVatPercentage: basketItem.vatPercentage,
            basketItemSubscriptionId: basketItem.subscriptionId,
            basketItemIsActive: basketItem.isActive,
            basketItemIsCancelled: basketItem.isCancelled,
            basketItemCancelledDate: basketItem.cancelledDate,
            basketItemCancelledBy: basketItem.cancelledBy,
            basketItemCancellationReason: basketItem.cancellationReason,
            basketItemTransactionItems: [],
            basketItemFirstBillingDate: basketItem.firstBillingDate,
            person: order.basket.person,
          };

          if (basketItem.productOption.courseId && basketItem.isActive) {

            courseProductOption = {
              productOptionId: basketItem.productOption.productOptionId,
              productName: basketItem.product.name,
              productOptionName: basketItem.productOption.name,
              courseId: basketItem.productOption.courseId,
              course: basketItem.productOption.course,
              friendlyId: basketItem.productOption.friendlyId,
              friendlyUrl: basketItem.productOption.course.friendlyUrl,
              purchaseDate: basketItem.updatedDate,
              startDate: basketItem.productOption.startDate,
            };

            courseProductOptions.push(courseProductOption);
          }

          const tItems: any = basketItem.transactionItems;

          tItems.sort((a: any, b: any) => {
            const dateA: any = new Date(a.createdDate);
            const dateB: any = new Date(b.createdDate);
            return dateB - dateA;
          });

          for (const tItem of tItems) {
            // exclude transactions for update payment method, resume subscription
            // or failed one-off transactions (i.e. only include failed recurring)
            if (tItem.transaction.notes.toLowerCase().indexOf('update') === -1 &&
              // tItem.transaction.notes.toLowerCase().indexOf('resume') === -1 &&
              tItem.transaction.notes.toLowerCase().indexOf('migrate') === -1 &&
              // tItem.transaction.notes.toLowerCase().indexOf('upgrade') === -1 &&
              tItem.transaction.amount !== 0 &&
              ((tItem.transaction.subscriptionId && tItem.transaction.billingStartDate)
                || tItem.transaction.invoiceNumber)) {
              transactionItem = {
                transactionItemId: tItem.transactionItemId,
                amount: tItem.transaction.amount > 0 &&
                  (basketItem.productOption.courseId || basketItem.productOption.eventId) ?
                  tItem.basketItem.price : tItem.transaction.amount,
                transaction: {
                  transactionDate: tItem.transaction.createdDate,
                  currency: tItem.transaction.currency,
                  amount: tItem.transaction.amount,
                  source: tItem.transaction.source,
                  status: tItem.transaction.status,
                  userNotes: tItem.transaction.userNotes,
                  invoiceNumber: tItem.transaction.invoiceNumber,
                  transactionId: tItem.transaction.transactionId,
                  billingStartDate: tItem.transaction.billingStartDate,
                  billingEndDate: tItem.transaction.billingEndDate,
                  // subscriptionId: tItem.transaction.subscriptionId,
                },
              };
              transactionItems.push(transactionItem);
            }
          }

          purchase.basketItemTransactionItems = transactionItems;

          // this.user.Purchases.push(purchase);
          userPurchases.push(purchase);
        }
      }
    }

    purchaseHistory.purchases = userPurchases;

    if (!forAdmin) {
      this.user.Purchases = userPurchases;
      this.user.CourseProductOptions = courseProductOptions;
      this.user.HadCommunity = hadCommunity;
      this.user.CancelledCommunity = cancelledCommunity;
      this.user.TrialCommunity = trialCommunity;
      this.user.Trial7Days = trial7days;
    } else {
      purchaseHistory.cancelledCommunity = cancelledCommunity;
      purchaseHistory.hadCommunity = hadCommunity;
      purchaseHistory.courseProductOptions = courseProductOptions;
      purchaseHistory.trialCommunity = trialCommunity;
      purchaseHistory.trial7days = trial7days;
      purchaseHistory.pendingCommunityCancellation = pendingCancelledCommunity;
    }

    // }
    console.log(purchaseHistory);
    return purchaseHistory;
  }

  async addAntiForgeryTokenAsync(): Promise<string> {

    this.antiForgeryDone = false;
    // const self = this;

    try {
      const response: Response =
        await this.httpClient.fetch(`${this.baseAddress}/antiforgerytokens?t=${Date.now()}`);
      // const reportData = this.sentry.toView(
        // 'AF Response', response);
      const token = await response.json();
      this.user.AntiForgeryToken = token.text;
      return Promise.resolve('OK');
    } catch (err) {
      const reportError = this.sentry.toView(
        'Identity error (addAntiForgeryTokenAsync)',
        err);
      return Promise.reject(err);
    }

  }

  async updateHttpClientHeadersAsync(): Promise<string> {

    const promise: any = new Promise((resolve, reject) => {
      console.log(this.user.AntiForgeryToken);
      this.httpClient.configure((config) => {
        config.withDefaults({
          headers: {
            __RequestVerificationToken: this.user.AntiForgeryToken,
            //'x-csrf-token-header-cpt': this.user.AntiForgeryToken,
            'x-csrf-token-header-cpt-sso': this.user.AntiForgeryToken,
            'Cache-Control': 'no-cache',
          },
          // cache: 'no-cache',
        });
      });
      setBreezeHeaders(this.user.AntiForgeryToken);
      this.ea.publish('antiForgeryDone', { value: true });
      this.antiForgeryDone = true;
      // console.log(this.httpClient);
      return resolve('OK');
    });

    return promise;
  }

  async initialiseIdentityAsync(): Promise<string> {

    const promise: any = new Promise(async (resolve, reject) => {

      try {

        // if (!this.antiForgeryDone || this.forceAntiForgeryUpdate) {
        await this.addAntiForgeryTokenAsync();
        await this.updateHttpClientHeadersAsync();
        // }

        this.forceAntiForgeryUpdate = false;

        return resolve('OK');

      } catch (err) {
        const reportError = this.sentry.toView(
          'Identity error (initialiseIdentityAsync)',
          err);
        return resolve(err);
      }

    });

    return promise;

  }

  async registerAsync(requestModel: IRegisterModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};

    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response =
          await this.httpClient.fetch(`${this.baseAddress}/register?t=${Date.now()}`, {
            method: 'POST',
            body: json(requestModel),
          });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = `Registration successful - ${result.UserName}`;
        } else {
          apiResponse.message = `Registration failed: ${result.message}`;

          const reportError = this.sentry.toView(
            `Identity error (registerAsync): ${requestModel.userName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        console.log(err);

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Registration failed: server API error. ' +
            'Please try again later.';
        }

        const reportError = this.sentry.toView(
          `Identity error (registerAsync): ${requestModel.userName}`,
          err);

        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async loginAsync(requestModel: ILoginModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};

    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/login?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = `Login successful - ${result.UserName}`;
          this.user.AntiForgeryToken = result.Token;
          await this.updateHttpClientHeadersAsync();
          // this.forceAntiForgeryUpdate = true;
          this.updateUserInfo(result);
        } else {
          const reportError = this.sentry.toView(
            `Identity error (loginAsync): ${requestModel.userName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (loginAsync): ${requestModel.userName}`,
          err);
        resolve(apiResponse);

      }

    });

    return promise;

  }

  async logoutAsync(): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    let response: Response;
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        response = await this.httpClient.fetch(`${this.baseAddress}/logout?t=${Date.now()}`, {
          method: 'POST',
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'You have successfully logged out.';
          this.user.AntiForgeryToken = result.Token;
          await this.updateHttpClientHeadersAsync();
          // this.forceAntiForgeryUpdate = true;
          this.updateUserInfo(result);
        } else {
          apiResponse.message = result.message;
          const reportError = this.sentry.toView(
            'Identity error (logoutAsync)',
            `Status code: ${response.status}. Message: ${result.message}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          'Identity error (logoutAsync)',
          err);

        const reportStatus = this.sentry.toView(
          `Repository (getData) user status: IsAuthenticated = ${this.user.IsAuthenticated}.
 UserName = ${this.user.UserName}. PersonId = ${this.user.PersonId}.
 IsActiveCommunity = ${this.user.IsActiveCommunity}`);

        const reportData = this.sentry.toView(
          `Logout status code: ${apiResponse.statusCode}`);

        // reportData = this.sentry.toView(
          // 'Logout response', response);

        return resolve(apiResponse);

      }

    });

    return promise;
  }

  async getUserInfoAsync(): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(
          `${this.baseAddress}/userInfo?t=${Date.now()}`, {
            method: 'GET',
          });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        // result.Person = toCamelCase(result.Person);

        if (apiResponse.statusCode === 200 && result.IsAuthenticated) {
          // this.status = '. User logged in - ' + result.UserName;
          this.updateUserInfo(result);
        } else {
          //  this.status = result.message;
          this.gotUserInfo = true;

          if (apiResponse.statusCode !== 200) {
            const reportError = this.sentry.toView(
              'Identity error (getUserInfoAsync)',
              `Status code: ${response.status}. Message: ${result.message}`);
          }

        }

        return resolve(apiResponse);

      } catch (err) {

        apiResponse.message = 'Failed to retrieve user info: server API error. ' +
          'Please try again later.';

        const reportError = this.sentry.toView(
          'Identity error (getUserInfoAsync)',
          err);

        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async testAsync(): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      const response = await
        this.httpClient.fetch('/api/task/sendInviteActivationsIfEnrollmentOpen?t=${Date.now()}', {
          method: 'GET',
        });

      console.log(response);
      apiResponse.statusCode = response.status;
      const result = await response.json();

      console.log(apiResponse.statusCode);
      console.log(result);
      return resolve(apiResponse);

    });

    return promise;

  }

  updateUserInfo(userInfo: IUserInfo) {

    const { IsAuthenticated, Roles, UserName, IsFullyConfirmed, PersonId,
      IsActiveCommunity, Person, CommunityExpired } = userInfo;

    this.user.IsAuthenticated = IsAuthenticated;
    this.user.Roles = Roles;
    this.user.UserName = UserName;
    this.user.IsFullyConfirmed = IsFullyConfirmed;
    this.user.PersonId = PersonId;
    this.user.IsActiveCommunity = IsActiveCommunity;
    this.user.CommunityExpired = CommunityExpired;

    // now null
    this.user.Person = Person;
    this.gotUserInfo = true;

  }

  isUserInRole(rolesAllowed: string) {

    let filteredRoles: never[] = [];

    if (this.user.Roles) {
      filteredRoles = this.user.Roles.filter((item: any) => {
        return rolesAllowed.indexOf(item) > -1;
      });
    }

    return filteredRoles.length > 0;
  }

  async requestResetAsync(userName: string, context: string): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/sendResetEmail?t=${Date.now()}`, {
          method: 'POST',
          body: json({ userName, context, emailAddress: userName }),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = `We have sent you an email containing a link to ${context === 'forgot' ? 'reset' : 'set'} your password. Please click this link and follow the instructions.`;
        } else {
          apiResponse.message = result.message;
          const reportError = this.sentry.toView(
            `Identity error (requestResetAsync): ${userName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (requestResetAsync): ${userName}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async resetPasswordAsync(requestModel: IResetPasswordModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/resetPassword?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = `You have successfully ${requestModel.context === 'reset' ? 'reset' : 'set'} your password. Please use this new password to login.`;
        } else {
          const reportError = this.sentry.toView(
            `Identity error (resetPasswordAsync) for personId: ${requestModel.personId}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (resetPasswordAsync) for personId: ${requestModel.personId}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async confirmAccountAsync(requestModel: IConfirmAccountModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/confirmAccount?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'You have successfully confirmed your account.';
        } else {
          const reportError = this.sentry.toView(
            `Identity error (confirmAccountAsync) for personId: ${requestModel.personId}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (confirmAccountAsync) for personId: ${requestModel.personId}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async confirmEmailChangeAsync(requestModel: IConfirmChangeEmailModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/confirmEmailChange?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'You have successfully changed your email address. Please log in with the new address on future visits.';
        } else {
          const reportError = this.sentry.toView(
            `Identity error (confirmEmailChangeAsync) for personId: ${requestModel.personId}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (confirmEmailChangeAsync) for personId: ${requestModel.personId}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async resendConfirmationAsync(context: string): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/resendConfirmation?t=${Date.now()}`, {
          method: 'POST',
          body: json({
            emailAddress: this.user.UserName, userName: this.user.UserName,
            resendContext: context,
          }),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'Confirmation email successfully sent - ' +
            'please click on the link in the email we have just sent you';
        } else {
          const reportError = this.sentry.toView(
            `Identity error (resendConfirmationAsync): ${this.user.UserName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (resendConfirmationAsync): ${this.user.UserName}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async changePasswordAsync(requestModel: IChangePasswordModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/changePassword?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'Successful - please use your new password the ' +
            'next time you log in';
        } else {
          const reportError = this.sentry.toView(
            `Identity error (changePasswordAsync) for personId: ${requestModel.personId}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (changePasswordAsync) for personId: ${requestModel.personId}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async changeUserNameAsync(requestModel: IChangeUserNameModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/changeUserName?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'IMPORTANT - we have sent a confirmation email to your new address. ' +
            'Your email address will not be changed until you have clicked the link in this email.';
          // this.user.IsFullyConfirmed = false;
          this.status = 'IMPORTANT - we have sent a confirmation email to your new address. ' +
            'Your email address will not be changed until you have clicked the link in this email.';
        } else {
          const reportError = this.sentry.toView(
            `Identity error (changeUserNameAsync): ${this.user.UserName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
          apiResponse.message = result.message;
        }

        this.status = result.message;

        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (changeUserNameAsync): ${this.user.UserName}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;
  }

  async checkNotExistingUserAsync(userName: string): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/checkNotExistingUser?t=${Date.now()}`, {
          method: 'POST',
          body: json({ userName }),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 400) {
          apiResponse.message = 'Email address already exists.';
        } else {
          apiResponse.message = 'OK';
        }

        if (apiResponse.statusCode === 500) {
          const reportError = this.sentry.toView(
            `Identity error (checkNotExistingUserAsync): ${userName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
        }

        return resolve(apiResponse);

      } catch (err) {

        // if (apiResponse.statusCode === 406) {
        // apiResponse.message = this.error406;
        // } else {
        // }
        // we let a server error here go as a duplicate email address or
        // CSRF error will fail later
        apiResponse.message = '';
        const reportError = this.sentry.toView(
          `Identity error (checkNotExistingUserAsync): ${userName}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

  async zoomRegister(requestModel: IZoomRegisterModel): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await this.httpClient.fetch(`${this.baseAddress}/zoomRegister?t=${Date.now()}`, {
          method: 'POST',
          body: json(requestModel),
        });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = 'Thanks, you have successfully registered for this webinar! Instructions on how to join the live call have been emailed to you.';
        } else {
          apiResponse.message = result.message;
          const reportError = this.sentry.toView(
            `Identity error, status not 200 (zoomRegister): ${this.user.UserName} for webinarId ${requestModel.webinarId}`,
            `Status code: ${response.status}. Message: ${result.message}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (zoomRegister), exception: ${this.user.UserName} for webinarId ${requestModel.webinarId}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;
  }

  isValidEmail(email: string): string {
    const re = /^([\w-]+(?:\.[\w-]+)*)@((?:[\w-]+\.)*\w[\w-]{0,66})\.([a-z]{2,20}(?:\.[a-z]{2})?)$/i;
    const valid = email && email.trim().length > 6 ? re.test(email.trim()) : false;
    return valid ? '' : 'Invalid email address.';
  }

  isValidPassword(password: string, context: string): string {

    let score = 0;

    if (!password || password.length < 8) {
      return 'Invalid password - min 8 characters.';
    }

    if (password.indexOf(' ') > -1) {
      return 'Invalid password - no spaces.';
    }

    if (password.length > 20) {
      return 'Invalid password - max 20 characters.';
    }

    if (context === 'login' || context === 'changepwcurrent' || context === 'changeusername') {
      return 'OK';
    }

        // registration password is checked for strength
        // award every unique letter until 5 repetitions
    const letters: any = new Object();

    for (let i = 0; i < password.length; i += 1) {
      letters[password[i]] = (letters[password[i]] || 0) + 1;
      score += 6.5 / letters[password[i]];
    }

      // bonus points for mixing it up
    const variations: any = {
      digits: /\d/.test(password),
      lower: /[a-z]/.test(password),
      upper: /[A-Z]/.test(password),
      nonWords: /\W/.test(password),
    };

    let variationCount = 0;

    for (const check in variations) {
      variationCount += (variations[check] === true) ? 1 : 0;
    }

    score += (variationCount - 1) * 10;

    return score < 50 || variationCount < 2 ? 'Invalid password - too weak, try another.' : 'OK';

  }

  async contactAsync(message: string): Promise<IApiResponse> {

    const apiResponse = <IApiResponse>{};
    const promise: Promise<IApiResponse> = new Promise(async (resolve, reject) => {

      try {

        const response = await
          this.httpClient.fetch(`${this.baseAddress}/contact?t=${Date.now()}`, {
            method: 'POST',
            body: json({ message }),
          });

        apiResponse.statusCode = response.status;
        const result = await response.json();

        if (apiResponse.statusCode === 200) {
          apiResponse.message = "Thanks for your message, we'll respond as soon as possible, to the email address you used to log into the site.";
        } else {
          apiResponse.message = result.message;
          const reportError = this.sentry.toView(
            `Identity error (contactAsync): ${this.user.UserName}`,
            `Status code: ${response.status}. Message: ${result.message}`);
        }
        return resolve(apiResponse);

      } catch (err) {

        if (apiResponse.statusCode === 406) {
          apiResponse.message = this.error406;
        } else {
          apiResponse.message = 'Sorry, there was a server error. Please try again later.';
        }
        const reportError = this.sentry.toView(
          `Identity error (contactAsync): ${this.user.UserName}`,
          err);
        return resolve(apiResponse);

      }

    });

    return promise;

  }

}
